import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { companyInfo } from '../data/navigation';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import './Contact.css';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xnjlgoaw';

interface FormData {
  name: string;
  email: string;
  phone: string;
  service: string;
  message: string;
}

/**
 * Contact page with form and company information
 * Features a clean form layout with service selection and success messaging
 */
export const Contact: React.FC = () => {
  useDocumentTitle('Contact');
  const formRef = useScrollReveal({ threshold: 0.1 });
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const services = [
    'Custom Painting',
    'Plaster & Venetian Plaster',
    'Wood Finishing',
    'Wallpaper Installation',
    'Maintenance Programs',
    'Epoxy Flooring',
    'Commercial Services',
    'Other',
  ];

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          _subject: 'New Mims Family Painting website inquiry',
        }),
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });

        setTimeout(() => {
          setIsSubmitted(false);
        }, 5000);
      } else {
        const data = await response.json().catch(() => null);
        setError(data?.errors?.[0]?.message || 'We could not send your inquiry. Please try again or contact us directly.');
      }
    } catch {
      setError('We could not send your inquiry. Please try again or contact us directly.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="contact-page page-enter">
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="contact-hero__content">
          <h1 className="contact-hero__title">Start a Conversation</h1>
        </div>
      </section>

      {/* Form & Info Section */}
      <section className="contact-section">
        <div className="contact-container">
          <div ref={formRef} className="contact-layout">
            {/* Form Column */}
            <div className="contact-form-wrapper">
              <h2 className="contact-form__heading">Tell us about the work</h2>

              {isSubmitted ? (
                <div className="contact-form__success" role="status">
                  <div className="contact-form__success-icon">✓</div>
                  <h3 className="contact-form__success-title">Message received</h3>
                  <p className="contact-form__success-text">
                    Thank you for getting in touch. We will review the details and follow up soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="contact-form__group">
                    <label htmlFor="name" className="contact-form__label">
                      Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="contact-form__input"
                      placeholder="Name"
                    />
                  </div>

                  <div className="contact-form__group">
                    <label htmlFor="email" className="contact-form__label">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="contact-form__input"
                      placeholder="you@example.com"
                    />
                  </div>

                  <div className="contact-form__group">
                    <label htmlFor="phone" className="contact-form__label">
                      Phone (optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="contact-form__input"
                      placeholder="(631) 555-0000"
                    />
                  </div>

                  <div className="contact-form__group">
                    <label htmlFor="service" className="contact-form__label">
                      Service Needed
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      required
                      className="contact-form__select"
                    >
                      <option value="">Choose a service</option>
                      {services.map((svc) => (
                        <option key={svc} value={svc}>
                          {svc}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="contact-form__group">
                    <label htmlFor="message" className="contact-form__label">
                      Project Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="contact-form__textarea"
                      placeholder="Location, scope, timing, and anything helpful to know"
                    />
                  </div>

                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    isLoading={isLoading}
                    className="contact-form__submit"
                  >
                    Send Inquiry
                  </Button>

                  {error && (
                    <p className="contact-form__error" role="alert">{error}</p>
                  )}
                </form>
              )}
            </div>

            {/* Info Column */}
            <div className="contact-info-wrapper">
              <div className="contact-info">
                <h2 className="contact-info__heading">Contact Details</h2>

                <div className="contact-info__item">
                  <p className="contact-info__label">Company</p>
                  <p className="contact-info__value">{companyInfo.name}</p>
                </div>

                <div className="contact-info__item">
                  <p className="contact-info__label">Phone</p>
                  <a href={`tel:${companyInfo.phone}`} className="contact-info__link">
                    {companyInfo.phone}
                  </a>
                </div>

                <div className="contact-info__item">
                  <p className="contact-info__label">Email</p>
                  <a href={`mailto:${companyInfo.email}`} className="contact-info__link">
                    {companyInfo.email}
                  </a>
                </div>

                <div className="contact-info__item">
                  <p className="contact-info__label">Service Area</p>
                  <p className="contact-info__value">{companyInfo.address}</p>
                </div>

                <div className="contact-info__divider" />

                <div className="contact-info__social">
                  <p className="contact-info__label">Follow Us</p>
                  <a
                    href={companyInfo.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact-info__social-link"
                  >
                    Instagram
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Area Section */}
      <section className="contact-service-area">
        <div className="contact-container">
          <div className="contact-service-area__content">
            <h2 className="contact-service-area__heading">Serving Long Island &amp; New York City</h2>
            <p className="contact-service-area__text">
              We work throughout Long Island and New York City with homeowners, design teams,
              contractors, and property managers. Reach out with the location and scope, and we
              will let you know how we can help.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

Contact.displayName = 'Contact';
